import { AggregatedCourseSessionDto, AggregatedSlotDto } from '../../../service/court-availability-service';
import { BlockReasonCode } from '../../../service/court-availability-service';
import { BookingType } from '../../../models/booking.model';
import { CourseEventType, CourseKind } from '../../../models/course-kind.model';

export type BookingViewMode = 'simplified' | 'day' | 'week' | 'day-v2' | 'week-v2';

/** 'day' and 'day-v2' share all data/fetch logic — they differ only in which
 *  grid component renders them (app-court-time-grid vs app-court-lanes-grid). */
export function isDayViewMode(mode: BookingViewMode): boolean {
  return mode === 'day' || mode === 'day-v2';
}

/** 'week' and 'week-v2' share all data/fetch logic — they differ only in which
 *  grid component renders them (app-court-time-grid vs app-court-lanes-grid). */
export function isWeekViewMode(mode: BookingViewMode): boolean {
  return mode === 'week' || mode === 'week-v2';
}

/**
 * Purely VISUAL inset applied at render time to every block rect in the
 * vertical time-axis grid — occupied/course/past segment blocks in
 * app-court-column and the selection overlay alike: +1px top, -2px height,
 * so back-to-back blocks show a hairline gap instead of touching (approved
 * mockup: `top:${top + 1}px; height:${h - 2}px`). Applied only at the final
 * pixel-rect step; all time math (startTimeUtc, durationMinutes, drag
 * snapping, tier thresholds) stays on the un-inset geometry. Free-cell click
 * targets keep FULL interval geometry — they tile the column continuously.
 */
export const BLOCK_INSET_TOP_PX = 1;
export const BLOCK_INSET_HEIGHT_PX = 2;

/**
 * A contiguous run of the same state for a single court on a single day.
 * Replaces the old dense per-interval CourtGridCell map — the vertical
 * time-axis grid renders merged blocks, not one DOM node per interval.
 *
 * kind 'past' is reserved for past AND available intervals (nothing to show
 * but the time cutoff). Past intervals that carry a booking/course keep their
 * real kind ('occupied'/'course'/'blocked') so the block renders with full
 * booking info, and are flagged via `isPast` for dimmed styling instead.
 *
 * kind 'blocked' is a Platzsperre (plan §6.3.4): the court is CLOSED, not
 * "booked by someone". It never carries a courseSession payload and is never
 * clickable — it reads from `eventType`/`displayName` alone.
 */
export interface CourtColumnSegment {
  startTimeUtc: string;
  endTimeUtc: string;
  kind: 'available' | 'occupied' | 'course' | 'blocked' | 'past';
  /**
   * Only meaningful when kind === 'occupied'. In practice only INDIVIDUAL or
   * SUBSCRIPTION — course-type bookings never reach 'occupied', they're
   * represented via kind === 'course' instead.
   */
  bookingType?: BookingType;
  blockedReason?: BlockReasonCode;
  /**
   * Stable id of the underlying booking/hold/course-session — used to merge
   * consecutive intervals of the SAME occupancy into one block, never merging
   * two different, merely-adjacent occupancies (see buildDayGrid()).
   *
   * Present for an ordinary booking or hold ('occupied'), for a joinable
   * session ('course'), and for a Platzsperre ('blocked'): a closure's id is
   * member-facing by design, so the backend hands it out.
   *
   * DELIBERATELY ABSENT for the other two suppressed occupancies — a
   * `visibility='private'` session and a session of a deactivated course.
   * `GET /sessions/:id` answers those ids with the same 404 as an id that
   * never existed (anti-enumeration, asserted by a backend e2e), so the
   * aggregated payload must not hand them out either. They arrive as kind
   * 'occupied' with no id, no displayName and no eventType — nothing about
   * them distinguishes one from another, which is exactly what the id-less
   * merge fallback in buildDayGrid() has to account for.
   */
  occupancyId?: string;
  /**
   * Only set for kind 'course': the full per-court course-session breakdown
   * (name, enrollment status, join policy, …) carried through the grid pivot
   * so the column can render the course name, the enrolled badge and emit the
   * session to the enrollment modal on click. A merged run keeps the FIRST
   * interval's session object — occupancyId === courseSession.id, so every
   * interval of one run describes the same session.
   */
  courseSession?: AggregatedCourseSessionDto;
  /**
   * Campaign kind behind a 'course'/'blocked' segment. 'course' segments read
   * it off `courseSession.kind`; 'blocked' segments have no payload and read
   * it off the CourtStateDto (or fall back to 'block' by construction).
   */
  courseKind?: CourseKind;
  /**
   * Label flavour behind a 'course'/'blocked' segment — with `courseKind` it
   * is the ONLY input to the plan §6.4.3 label (see court-segment-label.util).
   * `null`/undefined on a course segment means a plain Kurs.
   */
  eventType?: CourseEventType | null;
  /**
   * Effective, server-resolved name of the occupancy
   * (`session.title ?? course.name ?? fallback`, plan §6.4). The tile renders
   * THIS and never coalesces a name itself.
   */
  displayName?: string;
  /**
   * Unified "this block belongs to the current user" flag: true for occupied
   * segments whose CourtStateDto.isOwnBooking is set (requester-relative,
   * backend-provided) and for course segments where the user is enrolled
   * (courseSession.isUserEnrolled). Drives the corner "Ecke" own-booking
   * badge. A merged run keeps the FIRST interval's value — same occupancyId
   * means same booking, hence same owner across every interval of the run.
   */
  isOwn?: boolean;
  /**
   * Only set for 'occupied'/'course' kinds: true when EVERY interval of the
   * merged run lies in the past (AND-folded while extending a run in
   * buildDayGrid()). Drives the dimmed rendering of finished bookings — a
   * booking that spans "now" (some intervals past, some not) stays false and
   * renders at full color. Purely visual; interactivity/blocked-range math
   * never reads it (occupied/course segments are non-interactive either way).
   */
  isPast?: boolean;
  /** Anchor slot for pricing/duration lookups (first raw slot in the run). */
  aggregatedSlot: AggregatedSlotDto;
  // NOTE: booker name is intentionally never mapped here, even though the
  // backend CourtStateDto reserves a bookerName field for future admin use —
  // this customer-facing model must never surface another user's name.
}

/** Sorted, ms-based blocked range — mirrors the backend's BlockedRange shape. */
export interface BlockedRangeMs {
  startMs: number;
  endMs: number;
}

/** One court's column for one day. */
export interface CourtColumnModel {
  courtId: string;
  courtName: string;
  /** Sorted, merged — used for rendering occupied/course blocks. */
  segments: CourtColumnSegment[];
  /** Sorted ascending by startMs — used for O(n) early-exit drag-clamp checks. */
  blockedRanges: BlockedRangeMs[];
  dayStartUtc: string;
  dayEndUtc: string;
}

/** One day (the single day of Tagesansicht, or one of 7 in Wochenansicht). */
export interface DayGridModel {
  date: string; // YYYY-MM-DD, local/venue calendar date
  isToday: boolean;
  courts: CourtColumnModel[];
  timeColumns: string[];
}

/** Duration availability for a specific court on a specific day at a specific start time */
export interface CourtDurationOption {
  duration: number;
  available: boolean;
  endTimeUtc: string;
  price: number;
}

/** Emitted when the user commits a court + day + slot + duration selection in the grid */
export interface CourtGridSelectionEvent {
  courtId: string;
  courtName: string;
  /** Authoritative date this selection currently lives on (see booking-container reconciliation). */
  dateStr: string;
  startTimeUtc: string;
  endTimeUtc: string;
  durationMinutes: number;
  price: number;
  aggregatedSlot: AggregatedSlotDto;
  availableDurations: CourtDurationOption[];
}
